import { supabase } from "../../supabaseClient"
import { DEFAULT_ACCOUNT_PLAN } from "../constants/defaultAccountPlan"

export const saveAccountPlan = async (id = undefined, item) => {
    if (id) {
        return await supabase
            .from('AccountPlan')
            .update({ ...item })
            .match({ id })
    } else {
        return await supabase
            .from('AccountPlan')
            .insert([{ ...item }])
    }
}

export const loadAccountPlans = async (companyId) => {
    let { data, error } = await supabase
        .from('AccountPlan')
        .select('*')
        .eq("company_id", companyId)
        .is('deleted_at', null)
    if (!error) {
        const all = data || []

        // Fallback: contas semeadas antes do parent_id ser populado têm parent_id
        // null. Como o code reflete a hierarquia ("1.1" filho de "1"), inferimos
        // o pai aqui. Quando a empresa rodar o backfill SQL, este fallback vira no-op.
        const level1ByCode = new Map()
        all.forEach((a) => { if (a.level === 1) level1ByCode.set(String(a.code), a) })
        const enriched = all.map((a) => {
            if (a.level === 2 && !a.parent_id && a.code) {
                const dot = String(a.code).lastIndexOf('.')
                if (dot > 0) {
                    const parent = level1ByCode.get(String(a.code).slice(0, dot))
                    if (parent) return { ...a, parent_id: parent.id }
                }
            }
            return a
        })

        // Ordenação natural: "1, 1.1, 1.2, ..., 1.10" ao invés de alfabética
        // ("1, 1.1, 1.10, 1.2", que era o resultado do .order('code') do Supabase).
        return enriched.sort((a, b) =>
            String(a.code).localeCompare(String(b.code), 'pt-BR', { numeric: true })
        )
    }
    return []
}

export const deleteAccountPlan = async (id, userId) => {
    return await supabase
        .from('AccountPlan')
        .update({ deleted_at: new Date(), deleted_by: userId })
        .match({ id })
}

// Insere o Plano de Contas padrão para a empresa, se ainda não tiver nenhum.
// Idempotente: se já existe qualquer registro ativo de AccountPlan para a
// empresa, retorna { error: null, skipped: true } sem inserir nada.
//
// Insere em 2 passos para popular parent_id corretamente: primeiro os grupos
// (nível 1), depois as filhas (nível 2) já apontando para o id do pai.
export const seedDefaultAccountPlan = async ({ companyId, userId }) => {
    if (!companyId) {
        return { error: new Error('companyId obrigatório'), skipped: false }
    }

    const { data: existing, error: checkErr } = await supabase
        .from('AccountPlan')
        .select('id')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .limit(1)

    if (checkErr) return { error: checkErr, skipped: false }
    if (existing && existing.length > 0) return { error: null, skipped: true }

    const baseRow = (c) => ({
        company_id: companyId,
        code: c.code,
        name: c.name,
        type: c.type,
        level: c.level,
        status: 'ativa',
        created_by: userId,
    })

    // 1) Insere os grupos (nível 1)
    const level1Rows = DEFAULT_ACCOUNT_PLAN.filter((c) => c.level === 1).map(baseRow)
    const { error: l1Err } = await supabase.from('AccountPlan').insert(level1Rows)
    if (l1Err) return { error: l1Err, skipped: false }

    // 2) Lê os ids dos grupos recém-inseridos para resolver os parent_id
    const { data: parents, error: parentsErr } = await supabase
        .from('AccountPlan')
        .select('id, code')
        .eq('company_id', companyId)
        .eq('level', 1)
        .is('deleted_at', null)
    if (parentsErr) return { error: parentsErr, skipped: false }

    const idByCode = new Map((parents || []).map((p) => [String(p.code), p.id]))

    // 3) Insere as filhas (nível 2) com parent_id derivado do code "X.Y" → pai "X"
    const level2Rows = DEFAULT_ACCOUNT_PLAN.filter((c) => c.level === 2).map((c) => {
        const dot = String(c.code).lastIndexOf('.')
        const parentCode = dot > 0 ? String(c.code).slice(0, dot) : null
        return {
            ...baseRow(c),
            parent_id: parentCode ? (idByCode.get(parentCode) || null) : null,
        }
    })

    const { error: l2Err } = await supabase.from('AccountPlan').insert(level2Rows)
    return { error: l2Err || null, skipped: false }
}
